import { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Login from './pages/auth/Login';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import Dashboard from './pages/dashboard/Dashboard';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import UserList from './pages/users/UserList';
import CompanyList from './pages/companies/CompanyList';
import RequestList from './pages/requests/RequestList';
import RequestDetail from './pages/requests/RequestDetail';
import ConstructionList from './pages/constructions/ConstructionList';
import ConstructionDetail from './pages/constructions/ConstructionDetail';
import DailyReportList from './pages/daily-reports/DailyReportList';
import DailyReportDetail from './pages/daily-reports/DailyReportDetail';
import Calendar from './pages/calendar/Calendar';
import BoardList from './pages/board/BoardList';
import BoardDetail from './pages/board/BoardDetail';
import SurveyList from './pages/surveys/SurveyList';
import SurveyDetail from './pages/surveys/SurveyDetail';
import SurveyResults from './pages/surveys/SurveyResults';
import TaskList from './pages/tasks/TaskList';
import TalkRoomList from './pages/talkrooms/TalkRoomList';
import MasterList from './pages/masters/MasterList';
import GroupList from './pages/groups/GroupList';
import RoleManagement from './pages/admin/RoleManagement';
import PermissionMatrix from './pages/admin/PermissionMatrix';
import IncentiveSettings from './pages/admin/IncentiveSettings';
import Profile from './pages/profile/Profile';
import ChangePassword from './pages/profile/ChangePassword';
import DependentManagement from './pages/profile/DependentManagement';
import NotificationList from './pages/notifications/NotificationList';
import PayslipList from './pages/payslips/PayslipList';
import YearEndAdjustment from './pages/payroll/YearEndAdjustment';
import BillingInvoiceList from './pages/billing-invoices/BillingInvoiceList';
import BillingInvoiceDetail from './pages/billing-invoices/BillingInvoiceDetail';
import IncentiveStatementList from './pages/incentives/IncentiveStatementList';
import Loading from './components/common/Loading';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';

function App() {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loading size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/:token" element={<AcceptInvitation />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col ml-0 md:ml-[200px] transition-[margin] duration-300">
        <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto p-3 sm:p-4 md:p-6" style={{ backgroundColor: 'var(--background-main)' }}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard />} />
            
            {/* 権限保護されたルート */}
            <Route path="/users" element={
              <ProtectedRoute permission="users.view"><UserList /></ProtectedRoute>
            } />
            <Route path="/companies" element={
              <ProtectedRoute permission="companies.view"><CompanyList /></ProtectedRoute>
            } />
            <Route path="/requests" element={
              <ProtectedRoute permission="requests.view"><RequestList /></ProtectedRoute>
            } />
            <Route path="/requests/:id" element={
              <ProtectedRoute permission="requests.view"><RequestDetail /></ProtectedRoute>
            } />
            <Route path="/constructions" element={
              <ProtectedRoute permission="constructions.view"><ConstructionList /></ProtectedRoute>
            } />
            <Route path="/constructions/:id" element={
              <ProtectedRoute permission="constructions.view"><ConstructionDetail /></ProtectedRoute>
            } />
            <Route path="/billing-invoices" element={
              <ProtectedRoute permission="billing_invoices.view"><BillingInvoiceList /></ProtectedRoute>
            } />
            <Route path="/billing-invoices/:id" element={
              <ProtectedRoute permission="billing_invoices.view"><BillingInvoiceDetail /></ProtectedRoute>
            } />
            <Route path="/daily-reports" element={
              <ProtectedRoute permission="daily_reports.view"><DailyReportList /></ProtectedRoute>
            } />
            <Route path="/daily-reports/:id" element={
              <ProtectedRoute permission="daily_reports.view"><DailyReportDetail /></ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute permission="calendar.view"><Calendar /></ProtectedRoute>
            } />
            <Route path="/board" element={
              <ProtectedRoute permission="board.view"><BoardList /></ProtectedRoute>
            } />
            <Route path="/board/:id" element={
              <ProtectedRoute permission="board.view"><BoardDetail /></ProtectedRoute>
            } />
            <Route path="/surveys" element={
              <ProtectedRoute permission="surveys.view"><SurveyList /></ProtectedRoute>
            } />
            <Route path="/surveys/:id" element={
              <ProtectedRoute permission="surveys.view"><SurveyDetail /></ProtectedRoute>
            } />
            <Route path="/surveys/:id/results" element={
              <ProtectedRoute permission="surveys.view"><SurveyResults /></ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute permission="tasks.view"><TaskList /></ProtectedRoute>
            } />
            <Route path="/talkrooms" element={
              <ProtectedRoute permission="talkrooms.view"><TalkRoomList /></ProtectedRoute>
            } />
            <Route path="/payslips" element={
              <ProtectedRoute permission="payslips.view"><PayslipList /></ProtectedRoute>
            } />
            <Route path="/year-end-adjustment" element={
              <ProtectedRoute permission="year_end.view"><YearEndAdjustment /></ProtectedRoute>
            } />
            <Route path="/incentive-statements" element={
              <ProtectedRoute permission="incentives.view"><IncentiveStatementList /></ProtectedRoute>
            } />
            
            {/* マスタ管理 */}
            <Route path="/masters/:masterType" element={
              <ProtectedRoute permission="masters.view"><MasterList /></ProtectedRoute>
            } />
            <Route path="/groups" element={
              <ProtectedRoute permission="groups.view"><GroupList /></ProtectedRoute>
            } />
            
            {/* 管理者専用ルート */}
            <Route path="/admin/roles" element={
              <ProtectedRoute adminOnly><RoleManagement /></ProtectedRoute>
            } />
            <Route path="/admin/permissions" element={
              <ProtectedRoute adminOnly><PermissionMatrix /></ProtectedRoute>
            } />
            <Route path="/admin/incentive-settings" element={
              <ProtectedRoute adminOnly><IncentiveSettings /></ProtectedRoute>
            } />
            
            {/* プロフィール関連（権限不要） */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/dependents" element={<DependentManagement />} />
            <Route path="/notifications" element={<NotificationList />} />
            
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export default App;
